import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '../../../ui/dialog';
import { exportResource } from '../../../../lib/exportApi';

const ExportDateModal = ({ isOpen, toggle, params }) => {
  const [dates, setDates] = useState({ start_date: '', end_date: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDates({ start_date: '', end_date: '' });
      setError('');
    }
  }, [isOpen]);

  const handleStartChange = (e) => {
    const value = e.target.value;
    setError('');
    setDates((prev) => ({
      ...prev,
      start_date: value,
      end_date: prev.end_date && prev.end_date < value ? '' : prev.end_date,
    }));
  };

  const handleEndChange = (e) => {
    const value = e.target.value;
    if (dates.start_date && value < dates.start_date) {
      setError('End date cannot be earlier than start date.');
      return;
    }
    setError('');
    setDates((prev) => ({ ...prev, end_date: value }));
  };

  const handleExport = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await exportResource('orders', { ...params, ...dates });
      toggle();
    } catch (err) {
      setError('Export failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && toggle()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Export CSV File</DialogTitle>
          <DialogDescription>
            Optionally narrow the export to a date range. Leave blank to export
            everything.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleExport} className="grid gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="start_date"
                className="text-xs font-medium text-slate-600"
              >
                Start Date
              </label>
              <input
                type="date"
                id="start_date"
                className={inputClass}
                value={dates.start_date}
                onChange={handleStartChange}
              />
            </div>
            <div>
              <label
                htmlFor="end_date"
                className="text-xs font-medium text-slate-600"
              >
                End Date
              </label>
              <input
                type="date"
                id="end_date"
                className={inputClass}
                value={dates.end_date}
                min={dates.start_date}
                onChange={handleEndChange}
              />
            </div>
          </div>

          {error && (
            <p role="alert" className="text-xs font-medium text-rose-600 m-0">
              {error}
            </p>
          )}

          <DialogFooter>
            <button
              type="button"
              onClick={toggle}
              className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
            >
              Close
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60 transition-colors"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Export CSV
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ExportDateModal;
